import { useState, useCallback } from 'react';
import axios from 'axios';
import { Configuration } from '../../types';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// ============================================================================
// SHARED HOOKS
// ============================================================================

/**
 * Hook for managing job submission
 */
export const useJobSubmission = (workflowId: number) => {
  const [submitting, setSubmitting] = useState(false);

  const submitJob = useCallback(async (
    jobName: string,
    parameters: any,
    batchSize: number
  ): Promise<{ success: boolean; jobId?: number; error?: string }> => {
    if (!jobName.trim()) {
      return { success: false, error: 'Please enter a job name' };
    }

    if (batchSize < 1 || batchSize > 999999) {
      return { success: false, error: 'Batch size must be between 1 and 999999' };
    }

    try {
      setSubmitting(true);
      const response = await axios.post(`${API_BASE}/jobs`, {
        workflow_id: workflowId,
        job_name: jobName,
        parameters: parameters,
        batch_size: batchSize
      });

      return { success: true, jobId: response.data.id };
    } catch (error) {
      console.error('Failed to submit job:', error);
      return { success: false, error: 'Failed to submit job' };
    } finally {
      setSubmitting(false);
    }
  }, [workflowId]);

  return { submitJob, submitting };
};

/**
 * Hook for managing saved configurations
 */
export const useConfigurationManager = (workflowId: number) => {
  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchConfigurations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/workflows/${workflowId}/configurations`);
      setConfigurations(response.data);
    } catch (error) {
      console.error('Failed to fetch configurations:', error);
    } finally {
      setLoading(false);
    }
  }, [workflowId]);

  const saveConfiguration = useCallback(async (
    configName: string,
    parameterValues: any
  ): Promise<{ success: boolean; error?: string }> => {
    if (!configName.trim()) {
      return { success: false, error: 'Please enter a configuration name' };
    }

    try {
      await axios.post(`${API_BASE}/configurations`, {
        workflow_id: workflowId,
        config_name: configName,
        parameter_values: parameterValues
      });

      await fetchConfigurations();
      return { success: true };
    } catch (error) {
      console.error('Failed to save configuration:', error);
      return { success: false, error: 'Failed to save configuration' };
    }
  }, [workflowId, fetchConfigurations]);

  return {
    configurations,
    loading,
    fetchConfigurations,
    saveConfiguration
  };
};

// ============================================================================
// SHARED COMPONENTS
// ============================================================================

interface JobSubmitPanelProps {
  jobName: string;
  setJobName: (name: string) => void;
  batchSize: number;
  setBatchSize: (size: number) => void;
  onSubmit: () => void;
  submitting: boolean;
}

export const JobSubmitPanel: React.FC<JobSubmitPanelProps> = ({
  jobName,
  setJobName,
  batchSize,
  setBatchSize,
  onSubmit,
  submitting
}) => {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Generate</h3>

      <input
        type="text"
        placeholder="Job name"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
        value={jobName}
        onChange={(e) => setJobName(e.target.value)}
      />

      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Batch Size
        </label>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min="1"
            max="999999"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={batchSize}
            onChange={(e) => setBatchSize(Math.max(1, Math.min(999999, parseInt(e.target.value) || 1)))}
          />
          <span className="text-sm text-gray-500">images</span>
        </div>
        {batchSize > 1 && (
          <p className="text-xs text-gray-600 mt-1">
            Generate {batchSize} images with the same parameters but different random seeds
          </p>
        )}
      </div>

      <button
        onClick={onSubmit}
        disabled={submitting}
        className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold py-2 px-4 rounded-lg hover:from-green-600 hover:to-green-700 transition-all disabled:opacity-50"
      >
        {submitting ? 'Submitting...' : (batchSize > 1 ? `Generate ${batchSize} Images` : 'Submit Job')}
      </button>
    </div>
  );
};

interface ConfigurationPanelProps {
  configName: string;
  setConfigName: (name: string) => void;
  onSave: () => void;
  configurations: Configuration[];
  onLoad: (config: Configuration) => void;
}

export const ConfigurationPanel: React.FC<ConfigurationPanelProps> = ({
  configName,
  setConfigName,
  onSave,
  configurations,
  onLoad
}) => {
  return (
    <>
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Save Configuration</h3>
        <input
          type="text"
          placeholder="Configuration name"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
          value={configName}
          onChange={(e) => setConfigName(e.target.value)}
        />
        <button
          onClick={onSave}
          className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all"
        >
          Save Configuration
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Saved Configurations</h3>
        <div className="space-y-2">
          {configurations.map((config) => (
            <div
              key={config.id}
              className="bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg p-3 cursor-pointer transition-colors"
              onClick={() => onLoad(config)}
            >
              <div className="font-medium text-sm text-gray-900">{config.config_name}</div>
              <div className="text-xs text-gray-500">
                {new Date(config.created_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

interface WorkflowHeaderProps {
  workflowName: string;
  category: string;
  tags?: string[];
  isFavorite: boolean;
  onToggleFavorite: () => void;
  categoryIcon: string;
}

export const WorkflowHeader: React.FC<WorkflowHeaderProps> = ({
  workflowName,
  category,
  tags,
  isFavorite,
  onToggleFavorite,
  categoryIcon
}) => {
  return (
    <div className="flex justify-between items-center mb-8">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{categoryIcon}</span>
          <div>
            <h2 className="text-3xl font-bold text-gray-900">{workflowName}</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                {category}
              </span>
              {tags && tags.length > 0 && (
                <div className="flex gap-1">
                  {tags.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      className="bg-gray-200 text-gray-700 px-2 py-1 rounded text-xs"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={onToggleFavorite}
          className={`p-2 rounded-full transition-colors ${
            isFavorite
              ? 'text-yellow-500 hover:text-yellow-600'
              : 'text-gray-400 hover:text-yellow-500'
          }`}
        >
          {isFavorite ? '⭐' : '☆'}
        </button>
      </div>
      <a href="/" className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors">
        ← Back to Workflows
      </a>
    </div>
  );
};
